import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Store } from "@/api/storeService";
import { updateStore } from "@/api/adminService";
import { toast } from "sonner";

export interface EditableStore extends Omit<Store, 'categories' | 'prominentCategory'> {
  categories?: Array<{
    categoryId: string;
    category: {
      title: {
        en: string;
        ar: string;
      };
      slug: string;
    };
    relevanceScore: number;
  }>;
  prominentCategory?: {
    categoryId: string;
    category: {
      title: {
        en: string;
        ar: string;
      };
      slug: string;
    };
    relevanceScore: number;
  };
}

export const useStoreEditMode = ({ initialStore, onSaveSuccess }: { initialStore: Store; onSaveSuccess?: (updatedStore: Store) => void }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedStore, setEditedStore] = useState<EditableStore>(initialStore);
  const [hasChanges, setHasChanges] = useState(false);

  const queryClient = useQueryClient();

  // Mutation for updating store
  const updateStoreMutation = useMutation({
    mutationFn: ({ storeId, storeData }: { storeId: string; storeData: Partial<EditableStore> }) =>
      updateStore({ storeId, storeData }),
    onSuccess: (response) => {
      if (response.ok && response.data) {
        // Invalidate by _id (always) and by old slug (when page was accessed by slug)
        void queryClient.invalidateQueries({ queryKey: ["store", initialStore._id] });
        if (initialStore.slug) {
          void queryClient.invalidateQueries({ queryKey: ["store", initialStore.slug] });
        }

        // Reset edit state with the returned data
        setEditedStore(response.data as EditableStore);
        setIsEditMode(false);
        setHasChanges(false);

        toast.success("تم التحديث بنجاح", { description: "تم حفظ التغييرات على بيانات المتجر" });

        onSaveSuccess?.(response.data as Store);
      } else {
        toast.error("خطأ في التحديث", { description: response.error || "حدث خطأ أثناء حفظ التغييرات" });
      }
    },
    onError: (error) => {
      toast.error("خطأ في التحديث", { description: "حدث خطأ أثناء حفظ التغييرات" });
      console.error("Error updating store:", error);
    },
  });

  const enableEditMode = useCallback(() => {
    setIsEditMode(true);
    setEditedStore(initialStore);
    setHasChanges(false);
  }, [initialStore]);

  const cancelEdit = useCallback(() => {
    setIsEditMode(false);
    setEditedStore(initialStore);
    setHasChanges(false);
  }, [initialStore]);

  const updateField = useCallback((field: keyof EditableStore, value: unknown) => {
    setEditedStore((prev) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
    setHasChanges(true);
  }, []);

  const updateNestedField = useCallback((
    parentField: keyof EditableStore,
    childField: string,
    value: unknown
  ) => {
    setEditedStore((prev) => {
      const currentParentValue = prev[parentField];
      const updated = {
        ...prev,
        [parentField]: {
          ...(typeof currentParentValue === 'object' && currentParentValue !== null ? currentParentValue : {}),
          [childField]: value,
        },
      };
      return updated;
    });
    setHasChanges(true);
  }, []);

  const saveChanges = useCallback(() => {
    if (!hasChanges || !editedStore) return;

    // Prepare data for update - remove readonly fields
    const updateData = {
      name: editedStore.name,
      slug: editedStore.slug,
      description: editedStore.description,
      location: editedStore.location,
      platforms: editedStore.platforms,
      categories: editedStore.categories,
      prominentCategory: editedStore.prominentCategory,
      tags: editedStore.tags,
      featured: editedStore.featured,
      verified: editedStore.verified,
      logoUrl: editedStore.logoUrl,
      yetfarragRating: editedStore.yetfarragRating,
      paymentOptions: editedStore.paymentOptions,
      deliveryGovernorates: editedStore.deliveryGovernorates,
      workingHours: editedStore.workingHours,
      refundPolicy: editedStore.refundPolicy,
      topProducts: editedStore.topProducts,
    };

    updateStoreMutation.mutate({
      storeId: initialStore._id,
      storeData: updateData,
    });
  }, [editedStore, hasChanges, initialStore._id, updateStoreMutation]);

  return {
    isEditMode,
    editedStore,
    hasChanges,
    isLoading: updateStoreMutation.isPending,
    enableEditMode,
    cancelEdit,
    updateField,
    updateNestedField,
    saveChanges,
  };
};